import React, { useState, useEffect } from 'react';
import aiSummaryService from '../../utils/aiSummaryService';
import appScriptService from '../../utils/appScriptService';

const ConnectionDebug = () => {
  const [debugInfo, setDebugInfo] = useState({
    env: {},
    aiTest: null,
    appScriptTest: null,
    errors: []
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadEnvironmentInfo();
  }, []);

  const loadEnvironmentInfo = () => {
    const env = {
      AI_PROVIDER: process.env.REACT_APP_AI_PROVIDER,
      OPENAI_TOKEN: process.env.REACT_APP_OPENAI_TOKEN ? '설정됨 (****)' : '설정 안됨',
      GEMINI_TOKEN: process.env.REACT_APP_GEMINI_TOKEN ? '설정됨 (****)' : '설정 안됨',
      APPS_SCRIPT_URL: process.env.REACT_APP_APPS_SCRIPT_URL ? '설정됨' : '설정 안됨',
      APPS_SCRIPT_PREVIEW: process.env.REACT_APP_APPS_SCRIPT_URL?.substring(0, 50) + '...'
    };

    setDebugInfo(prev => ({ ...prev, env }));
  };

  const testAIConnection = async () => {
    setLoading(true);
    try {



      
      const result = await aiSummaryService.validateAPIKey();

      
      setDebugInfo(prev => ({
        ...prev,
        aiTest: {
          success: result,
          message: result ? 'AI 서비스 연결 성공' : 'AI 서비스 연결 실패',
          provider: process.env.REACT_APP_AI_PROVIDER,
          timestamp: new Date().toLocaleString()
        }
      }));
    } catch (error) {
      console.error('AI 테스트 오류:', error);
      setDebugInfo(prev => ({
        ...prev,
        aiTest: {
          success: false,
          message: 'AI 테스트 중 오류: ' + error.message,
          error: error.toString(),
          timestamp: new Date().toLocaleString()
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const testAppScriptConnection = async () => {
    setLoading(true);
    try {


      
      const result = await appScriptService.testConnection();

      
      setDebugInfo(prev => ({
        ...prev,
        appScriptTest: {
          success: result,
          message: result ? 'Apps Script 연결 성공' : 'Apps Script 연결 실패',
          url: process.env.REACT_APP_APPS_SCRIPT_URL,
          timestamp: new Date().toLocaleString()
        }
      }));
    } catch (error) {
      console.error('Apps Script 테스트 오류:', error);
      setDebugInfo(prev => ({
        ...prev,
        appScriptTest: {
          success: false,
          message: 'Apps Script 테스트 중 오류: ' + error.message,
          error: error.toString(),
          timestamp: new Date().toLocaleString()
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const testDirectAppScript = async () => {
    setLoading(true);
    try {

      
      const baseUrl = process.env.REACT_APP_APPS_SCRIPT_URL;

      
      // 여러 가지 방법으로 테스트
      const testMethods = [
        {
          name: 'GET with action=ping',
          request: () => fetch(`${baseUrl}?action=ping`, {
            method: 'GET',
            mode: 'cors'
          })
        },
        {
          name: 'GET no-cors mode',
          request: () => fetch(`${baseUrl}?action=ping`, {
            method: 'GET',
            mode: 'no-cors'
          })
        },
        {
          name: 'POST with JSON',
          request: () => fetch(baseUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action: 'ping' })
          })
        },
        {
          name: 'Simple GET',
          request: () => fetch(baseUrl, {
            method: 'GET',
            mode: 'cors'
          })
        }
      ];

      for (const method of testMethods) {
        try {

          const response = await method.request();
          
          console.log(`${method.name} 응답:`, {
            status: response.status,
            statusText: response.statusText,
            type: response.type,
            ok: response.ok,
            headers: [...response.headers.entries()]
          });

          if (response.type === 'opaque') {
            setDebugInfo(prev => ({
              ...prev,
              errors: [...(prev.errors || []), {
                type: method.name,
                message: 'no-cors 모드: 연결 성공 (응답 확인 불가)',
                timestamp: new Date().toLocaleString()
              }]
            }));
            continue;
          }

          const text = await response.text();

          
          try {
            const data = JSON.parse(text);
            setDebugInfo(prev => ({
              ...prev,
              errors: [...(prev.errors || []), {
                type: method.name + ' SUCCESS',
                message: JSON.stringify(data, null, 2),
                timestamp: new Date().toLocaleString()
              }]
            }));
            break; // 성공하면 다음 테스트는 건너뛰기
          } catch (parseError) {
            setDebugInfo(prev => ({
              ...prev,
              errors: [...(prev.errors || []), {
                type: method.name + ' PARSE ERROR',
                message: `응답: ${text.substring(0, 200)}...`,
                timestamp: new Date().toLocaleString()
              }]
            }));
          }
          
        } catch (methodError) {
          console.error(`${method.name} 실패:`, methodError);
          setDebugInfo(prev => ({
            ...prev,
            errors: [...(prev.errors || []), {
              type: method.name + ' ERROR',
              message: methodError.toString(),
              timestamp: new Date().toLocaleString()
            }]
          }));
        }
      }
      
    } catch (error) {
      console.error('전체 테스트 오류:', error);
      setDebugInfo(prev => ({
        ...prev,
        errors: [...(prev.errors || []), {
          type: 'Complete Test Error',
          message: error.toString(),
          timestamp: new Date().toLocaleString()
        }]
      }));
    } finally {
      setLoading(false);
    }
  };

  const clearLogs = () => {
    setDebugInfo(prev => ({ ...prev, errors: [] }));
  };

  const testGeminiDirect = async () => {
    setLoading(true);
    try {
      const apiKey = process.env.REACT_APP_GEMINI_TOKEN;
      const url = `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`;
      

      
      const response = await fetch(url);

      
      const data = await response.json();

      
      setDebugInfo(prev => ({
        ...prev,
        errors: [...(prev.errors || []), {
          type: 'Gemini Direct Test',
          message: response.ok ? 'Gemini API 직접 호출 성공' : 'Gemini API 오류: ' + JSON.stringify(data),
          timestamp: new Date().toLocaleString()
        }]
      }));
    } catch (error) {
      console.error('Gemini 직접 테스트 오류:', error);
      setDebugInfo(prev => ({
        ...prev,
        errors: [...(prev.errors || []), {
          type: 'Gemini Direct Error',
          message: error.toString(),
          timestamp: new Date().toLocaleString()
        }]
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">🔧 연결 상태 디버깅</h2>

      {/* 환경변수 정보 */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-3">📋 환경변수 상태</h3>
        <div className="bg-gray-50 p-4 rounded">
          <pre className="text-sm">
            {JSON.stringify(debugInfo.env, null, 2)}
          </pre>
        </div>
      </div>

      {/* 테스트 버튼들 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <button
          onClick={testAIConnection}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300"
        >
          🤖 AI 테스트
        </button>
        <button
          onClick={testAppScriptConnection}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300"
        >
          📊 Apps Script 테스트
        </button>
        <button
          onClick={testDirectAppScript}
          disabled={loading}
          className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-300"
        >
          🔗 직접 호출 테스트
        </button>
        <button
          onClick={testGeminiDirect}
          disabled={loading}
          className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-300"
        >
          💎 Gemini 직접 테스트
        </button>
      </div>

      {/* AI 테스트 결과 */}
      {debugInfo.aiTest && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">🤖 AI 서비스 테스트 결과</h3>
          <div className={`p-4 rounded ${debugInfo.aiTest.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className={`font-semibold ${debugInfo.aiTest.success ? 'text-green-800' : 'text-red-800'}`}>
              {debugInfo.aiTest.success ? '✅' : '❌'} {debugInfo.aiTest.message}
            </div>
            <div className="text-sm text-gray-600 mt-2">
              <div>Provider: {debugInfo.aiTest.provider}</div>
              <div>시간: {debugInfo.aiTest.timestamp}</div>
              {debugInfo.aiTest.error && (
                <div className="text-red-600 mt-2">오류: {debugInfo.aiTest.error}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Apps Script 테스트 결과 */}
      {debugInfo.appScriptTest && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">📊 Apps Script 테스트 결과</h3>
          <div className={`p-4 rounded ${debugInfo.appScriptTest.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div className={`font-semibold ${debugInfo.appScriptTest.success ? 'text-green-800' : 'text-red-800'}`}>
              {debugInfo.appScriptTest.success ? '✅' : '❌'} {debugInfo.appScriptTest.message}
            </div>
            <div className="text-sm text-gray-600 mt-2">
              <div>URL: {debugInfo.appScriptTest.url}</div>
              <div>시간: {debugInfo.appScriptTest.timestamp}</div>
              {debugInfo.appScriptTest.error && (
                <div className="text-red-600 mt-2">오류: {debugInfo.appScriptTest.error}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 오류 로그 */}
      {debugInfo.errors && debugInfo.errors.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">📝 디버그 로그</h3>
            <button
              onClick={clearLogs}
              className="px-3 py-1 text-sm bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              로그 지우기
            </button>
          </div>
          <div className="bg-black text-green-400 p-4 rounded font-mono text-sm max-h-60 overflow-y-auto">
            {debugInfo.errors.map((error, index) => (
              <div key={index} className="mb-2">
                <span className="text-yellow-400">[{error.timestamp}]</span>{' '}
                <span className="text-blue-400">{error.type}:</span>{' '}
                <span>{error.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 해결 방법 가이드 */}
      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded">
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">💡 문제 해결 가이드</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• <strong>AI 연결 실패</strong>: API 키가 올바른지, provider 이름이 정확한지 확인</li>
          <li>• <strong>Apps Script 연결 실패</strong>: URL이 정확한지, 웹 앱이 올바르게 배포되었는지 확인</li>
          <li>• <strong>CORS 오류</strong>: Apps Script에서 CORS 헤더가 올바르게 설정되었는지 확인</li>
          <li>• <strong>환경변수 미반영</strong>: 개발 서버 재시작 필요 (npm start)</li>
        </ul>
      </div>
    </div>
  );
};

export default ConnectionDebug;